/* eslint-disable */
import { useEffect, useState } from 'react'
import { useApiGet } from '../../apiCalls/useApi'
import { getAdminManagedResources } from '../../apiCalls/permissionApi'
import { styled } from '@mui/material/styles'
import { TreeItem as MuiTreeItem, SimpleTreeView } from '@mui/x-tree-view'
import { Checkbox, ToggleButton, ToggleButtonGroup, Paper, CircularProgress } from '@mui/material'
import LoadingCircle from '../../components/LoadingCircle.tsx'

interface Permission {
  scope: string
  scope_id: string
  scope_name?: string
  role: string
}

interface User {
  id: string
  email: string
  permission: Permission[]
}

interface BaseNode {
  id: string
  name: string
  originalId?: string
  nodeType?: string
  businessId?: string
  canBeSelected: boolean
  canAssignAdmin: boolean
}

interface Application extends BaseNode {
  id: string
  orgId: string
  customerId: string
  name: string
  canBeSelected: boolean
  canAssignAdmin: boolean
}

interface Organization extends BaseNode {
  id: string
  customerId: string
  customerName: string
  name: string
  applications: Application[]
  canBeSelected: boolean
  canAssignAdmin: boolean
}

interface Customer extends BaseNode {
  id: string
  name: string
  organizations: Organization[]
  canBeSelected: boolean
  canAssignAdmin: boolean
}

interface ResourceTreeViewProps {
  isEditing: boolean
  user: User
  setPermissionsObjectToApiRequest: (permissions: any) => void
  resetFlag: number
}

const StyledTreeItem = styled(MuiTreeItem)(({ theme }) => ({
  '& .MuiTreeItem-content': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}))

const StyledToggleButton = styled(ToggleButton, {
  shouldForwardProp: (prop) => prop !== 'editing',
})<{ editing: boolean }>(({ theme, editing }) => ({
  '&.Mui-selected': {
    backgroundColor: editing ? theme.palette.primary.dark : theme.palette.grey[600],
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: editing ? theme.palette.primary.dark : theme.palette.grey[600],
    },
  },
  '&:not(.Mui-selected)': {
    backgroundColor: editing ? theme.palette.grey[600] : theme.palette.grey[300],
    color: theme.palette.primary.contrastText,
  },
  '&.Mui-disabled.Mui-selected': {
    backgroundColor: editing ? theme.palette.action.disabledBackground : theme.palette.grey[600],
    color: editing ? theme.palette.action.disabled : theme.palette.primary.contrastText,
  },
  '&.Mui-disabled:not(.Mui-selected)': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}))

export default function ResourceTreeView({
  isEditing,
  user,
  setPermissionsObjectToApiRequest,
  resetFlag,
}: ResourceTreeViewProps) {
  const [resources, setResources] = useState<Customer[] | null>(null)
  const [defaultExpandedItems, setDefaultExpandedItems] = useState<string[]>([])
  const [selectedPermissions, setSelectedPermissions] = useState<Record<string, boolean>>({})
  const [disabledPermissions, setDisabledPermissions] = useState<Record<string, boolean>>({})
  const [roleSelections, setRoleSelections] = useState<Record<string, string>>({})
  const fetchAdminManagedResources = useApiGet(getAdminManagedResources)

  const collectAllIds = (node: Customer | Organization | Application): string[] => {
    let ids = [node.id]
    if ('organizations' in node) {
      ids = ids.concat(...node.organizations.map((org) => collectAllIds(org)))
    }
    if ('applications' in node) {
      ids = ids.concat(...node.applications.map((app) => collectAllIds(app)))
    }
    return ids
  }

  const generatePermissionsObject = (
    resources: Customer[] | null,
    selectedPermissions: Record<string, boolean>,
    roleSelections: Record<string, string>
  ) => {
    const permissions: any[] = []

    const traverse = (node: Customer | Organization | Application, parentCustomerId?: string) => {
      const businessId = node.businessId || node.id
      const originalId = node.originalId || node.id

      let currentCustomerId = parentCustomerId

      if ('organizations' in node) {
        currentCustomerId = originalId
      }

      if (selectedPermissions[businessId]) {
        let scopeType = ''
        if ('organizations' in node) {
          scopeType = 'CUS'
        } else if ('applications' in node) {
          scopeType = 'ORG'
        } else {
          scopeType = 'APP'
        }

        const role = roleSelections[businessId] || 'User'

        let customerId = currentCustomerId || ''
        if ('customerId' in node && node.customerId) {
          customerId = node.customerId
        }

        permissions.push({
          scopeType: scopeType,
          scopeID: originalId,
          role: role.toUpperCase(),
          customerId: customerId,
        })
      }

      if ('organizations' in node && node.organizations) {
        node.organizations.forEach((org) => traverse(org, currentCustomerId))
      }
      if ('applications' in node && node.applications) {
        node.applications.forEach((app) => traverse(app, currentCustomerId))
      }
    }

    resources?.forEach((customer) => traverse(customer))

    return {
      userId: user.id,
      permissions,
    }
  }

  const initializeState = () => {
    const initialPermissions: Record<string, boolean> = {}
    const initialDisabled: Record<string, boolean> = {}
    const initialRoleSelections: Record<string, string> = {}

    const scopeTypeMapping: Record<string, string> = {}

    const mapIds = (node: Customer | Organization | Application) => {
      if (node.originalId && node.businessId) {
        let scopeType = ''
        if ('organizations' in node) {
          scopeType = 'CUS'
        } else if ('applications' in node) {
          scopeType = 'ORG'
        } else {
          scopeType = 'APP'
        }

        const mappingKey = `${scopeType}_${node.originalId}`
        scopeTypeMapping[mappingKey] = node.businessId
      }

      if ('organizations' in node && node.organizations) {
        node.organizations.forEach((org) => mapIds(org))
      }
      if ('applications' in node && node.applications) {
        node.applications.forEach((app) => mapIds(app))
      }
    }

    resources?.forEach((customer) => mapIds(customer))

    if (user && user.permission) {
      user.permission.forEach((perm: Permission) => {
        const mappingKey = `${perm.scope}_${perm.scope_id}`

        const businessId = scopeTypeMapping[mappingKey]

        if (businessId) {
          initialPermissions[businessId] = true
          initialRoleSelections[businessId] = perm.role.charAt(0) + perm.role.slice(1).toLowerCase()
        } else {
          console.warn(`did not find permission mapping: ${mappingKey}`)
        }
      })
    }

    const updateDisabledPermissions = (
      node: Customer | Organization | Application,
      parentSelected: boolean
    ) => {
      const businessId = node.businessId || node.id
      const isSelected = initialPermissions[businessId] || false
      const currentSelected = parentSelected || isSelected

      if (currentSelected) {
        if ('organizations' in node) {
          node.organizations.forEach((org) => {
            const orgBusinessId = org.businessId || org.id
            initialDisabled[orgBusinessId] = true
            updateDisabledPermissions(org, true)
          })
        }
        if ('applications' in node) {
          node.applications.forEach((app) => {
            const appBusinessId = app.businessId || app.id
            initialDisabled[appBusinessId] = true
          })
        }
      } else {
        if ('organizations' in node) {
          node.organizations.forEach((org) => {
            const orgBusinessId = org.businessId || org.id
            initialDisabled[orgBusinessId] = false
            updateDisabledPermissions(org, false)
          })
        }
        if ('applications' in node) {
          node.applications.forEach((app) => {
            const appBusinessId = app.businessId || app.id
            initialDisabled[appBusinessId] = false
          })
        }
      }
    }

    resources?.forEach((customer) => {
      updateDisabledPermissions(customer, false)
    })

    setSelectedPermissions(initialPermissions)
    setDisabledPermissions(initialDisabled)
    setRoleSelections(initialRoleSelections)

    const permissionsObj = generatePermissionsObject(
      resources,
      initialPermissions,
      initialRoleSelections
    )
    setPermissionsObjectToApiRequest(permissionsObj)
  }

  useEffect(() => {
    if (resources) {
      initializeState()
    }
  }, [resources, user, resetFlag])

  const fetchResources = async () => {
    const res = await fetchAdminManagedResources()
    if (!res || !res.customers) return

    const newResources = JSON.parse(JSON.stringify(res.customers || []))

    const processNode = (node: any, nodeType: string) => {
      node.originalId = node.id
      node.nodeType = nodeType
      node.businessId = `${nodeType}_${node.id}`
      node.id = `${nodeType}_${node.id}`

      if (nodeType === 'cus' && node.organizations) {
        node.organizations.forEach((org: any) => processNode(org, 'org'))
      }
      if ((nodeType === 'cus' || nodeType === 'org') && node.applications) {
        node.applications.forEach((app: any) => processNode(app, 'app'))
      }
    }

    newResources.forEach((customer: any) => processNode(customer, 'cus'))

    setResources(newResources)

    const allIds = newResources.flatMap((customer: any) => collectAllIds(customer))
    setDefaultExpandedItems(allIds)
  }

  useEffect(() => {
    fetchResources()
  }, [user])

  const handleToggle = (id: string, node: Customer | Organization | Application) => {
    if (!isEditing) return

    const businessId = node.businessId || id

    const newSelectedPermissions = { ...selectedPermissions }
    const newDisabledPermissions = { ...disabledPermissions }
    const newRoleSelections = { ...roleSelections }

    const isChecked = !selectedPermissions[businessId]
    newSelectedPermissions[businessId] = isChecked

    if (isChecked && !newRoleSelections[businessId]) {
      newRoleSelections[businessId] = 'User'
    } else if (!isChecked) {
      delete newRoleSelections[businessId]
    }

    const updateChildren = (
      node: Customer | Organization | Application,
      parentSelected: boolean
    ) => {
      const childBusinessId = node.businessId || node.id
      const isDisabled = parentSelected || false

      if ('organizations' in node) {
        node.organizations.forEach((org) => {
          const orgBusinessId = org.businessId || org.id
          newDisabledPermissions[orgBusinessId] = isDisabled
          if (isDisabled) {
            newSelectedPermissions[orgBusinessId] = false
            delete newRoleSelections[orgBusinessId]
          }
          updateChildren(org, isDisabled)
        })
      }
      if ('applications' in node) {
        node.applications.forEach((app) => {
          const appBusinessId = app.businessId || app.id
          newDisabledPermissions[appBusinessId] = isDisabled
          if (isDisabled) {
            newSelectedPermissions[appBusinessId] = false
            delete newRoleSelections[appBusinessId]
          }
        })
      }
    }

    updateChildren(node, isChecked)

    setSelectedPermissions(newSelectedPermissions)
    setDisabledPermissions(newDisabledPermissions)
    setRoleSelections(newRoleSelections)

    const permissionsObj = generatePermissionsObject(
      resources,
      newSelectedPermissions,
      newRoleSelections
    )
    setPermissionsObjectToApiRequest(permissionsObj)
  }

  const handleRoleChange = (id: string, newRole: string | null) => {
    if (!isEditing) return

    if (newRole) {
      const updatedRoles = { ...roleSelections, [id]: newRole }
      setRoleSelections(updatedRoles)

      const permissionsObj = generatePermissionsObject(resources, selectedPermissions, updatedRoles)
      setPermissionsObjectToApiRequest(permissionsObj)
    }
  }

  const renderTree = (node: Customer | Organization | Application) => {
    const isCustomerNode = 'organizations' in node
    const businessId = node.businessId || node.id

    return (
      <StyledTreeItem
        key={node.id}
        itemId={node.id}
        label={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={!!selectedPermissions[businessId]}
              onChange={() => handleToggle(node.id, node)}
              onClick={(event) => event.stopPropagation()}
              disabled={!isEditing || disabledPermissions[businessId] || !node.canBeSelected}
            />
            <span>{node.name}</span>
            <ToggleButtonGroup
              value={roleSelections[businessId] || null}
              exclusive
              onChange={(event, newRole) => handleRoleChange(node.id, newRole)}
              onClick={(event) => event.stopPropagation()}
              size='small'
              style={{ marginLeft: '10px' }}
              disabled={
                !isEditing || disabledPermissions[businessId] || !selectedPermissions[businessId]
              }
            >
              {isCustomerNode && node.canAssignAdmin && (
                <StyledToggleButton
                  value='Superadmin'
                  editing={isEditing}
                  disabled={
                    !isEditing ||
                    disabledPermissions[businessId] ||
                    !selectedPermissions[businessId]
                  }
                >
                  {'SuperAdmin'}
                </StyledToggleButton>
              )}
              {node.canAssignAdmin && (
                <StyledToggleButton
                  value='Admin'
                  editing={isEditing}
                  disabled={
                    !isEditing ||
                    disabledPermissions[businessId] ||
                    !selectedPermissions[businessId]
                  }
                >
                  {'Admin'}
                </StyledToggleButton>
              )}
              <StyledToggleButton
                value='User'
                editing={isEditing}
                disabled={
                  !isEditing || disabledPermissions[businessId] || !selectedPermissions[businessId]
                }
              >
                {'User'}
              </StyledToggleButton>
            </ToggleButtonGroup>
          </div>
        }
      >
        {'organizations' in node && node.organizations.map((org: Organization) => renderTree(org))}
        {'applications' in node && node.applications.map((app: Application) => renderTree(app))}
      </StyledTreeItem>
    )
  }

  return (
    <Paper
      variant='outlined'
      style={{
        marginTop: '30px',
        height: '53vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      {resources ? (
        <SimpleTreeView
          defaultExpandedItems={defaultExpandedItems}
          sx={{
            '& .MuiTreeItem-content': {
              '&:focus': {
                outline: 'none',
                backgroundColor: 'transparent',
              },
              '&.Mui-focused': {
                outline: 'none',
                backgroundColor: 'transparent',
              },
              '&.Mui-selected': {
                outline: 'none',
                backgroundColor: 'transparent',
              },
            },
          }}
        >
          {resources.map((customer) => renderTree(customer))}
        </SimpleTreeView>
      ) : (
        <div
          style={{
            height: '53vh',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <LoadingCircle />
        </div>
      )}
    </Paper>
  )
}
